/**
 * Initial geometry for manual tracing (no AI suggestion available).
 * Seeds a centered ellipse with the nose up so the user only drags points.
 */

const CONTOUR_POINTS = 24;
const CENTER = { x: 0.5, y: 0.5 };
const RADIUS_X = 0.28;
const RADIUS_Y = 0.34;
// Seeded landmarks sit just outside the contour: nose above the top, ears beside the sides.
const NOSE_OFFSET = 0.04;
const EAR_OFFSET = 0.03;

const round = (value) => Math.round(value * 10000) / 10000;
const clamp01 = (value) => Math.min(1, Math.max(0, value));
const isPoint = (p) => Boolean(p) && Number.isFinite(p.x) && Number.isFinite(p.y);

/**
 * Default protocol v2 landmarks from the contour bounding box: nose above the top,
 * ears (anterior helix) just outside the left and right extremes, at the box centre height.
 * Also used to fill landmarks missing from an AI suggestion.
 * @param {Array<{x:number,y:number}>} contour Normalized contour.
 * @returns {{ nose: object, earLeft: object, earRight: object }|null} `null` without a usable contour.
 */
export const createLandmarksFromContour = (contour) => {
  const points = (contour || []).filter(isPoint);
  if (points.length < 3) return null;

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + Math.max(...ys)) / 2;
  const point = (x, y) => ({ x: round(clamp01(x)), y: round(clamp01(y)), confidence: null });

  return {
    nose: point(centerX, minY - NOSE_OFFSET),
    earLeft: point(minX - EAR_OFFSET, centerY),
    earRight: point(maxX + EAR_OFFSET, centerY),
  };
};

/**
 * @returns {{ landmarks: { nose: object, earLeft: object, earRight: object },
 *   cranialContour: Array<{x:number,y:number}>, wasManuallyEdited: boolean, source: 'manual' }}
 */
export const createManualSeedGeometry = () => {
  const cranialContour = [];

  // Starts at the top (nose side) and walks clockwise, like the AI contour.
  for (let i = 0; i < CONTOUR_POINTS; i++) {
    const angle = -Math.PI / 2 + (i / CONTOUR_POINTS) * Math.PI * 2;
    cranialContour.push({
      x: round(CENTER.x + RADIUS_X * Math.cos(angle)),
      y: round(CENTER.y + RADIUS_Y * Math.sin(angle)),
    });
  }

  return {
    landmarks: createLandmarksFromContour(cranialContour),
    cranialContour,
    wasManuallyEdited: true,
    source: "manual",
  };
};
